import React, { useEffect, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Button, Col, Drawer, Row, Select, Checkbox, Divider, Spin, Upload, Input, Typography, Radio, DatePicker } from "antd";
import { EnvironmentOutlined, InboxOutlined, PhoneOutlined, UploadOutlined } from "@ant-design/icons";
import { validateMessages, fileTypeAllow } from "../../utils/StaticDataUtility";
import { connect } from "react-redux";
import { getCadidateResume, applyJob, updateAboutProfile, updateJobApplication, updateProfile, fetchSelectLookupList, fetchSelectStateLookupList } from "../../services/common.service";
import DebounceSelectLookup from "../../lookup/select-debounce-lookup";
import { formatAddress } from "../../utils/ActionUtility";
import { uploadResume, getPersonInfo, sendCAQEMail } from "../../services/user.service";
import { CustomeNotification } from "../../utils/NotificationUtility";
import "./ApplyJobModelPage.css";
import environment from "../../environments/environment";
import Title from "antd/es/typography/Title";
import { postHogCaptureAnalitics } from "../../services/posthog";
import { UserCacheUpdate } from "../../state/actions/auth";
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import type { RcFile } from "antd/es/upload/interface";
import type { Dayjs } from "dayjs";

const { Text } = Typography;

export interface IProps {
    isModalOpen: any,
    onCloseModal: () => void,
    type: string,
    title: string,
    user: any
    jobOrderId: any,
    selectedJob: any,
    updateUserCache: (payload: any) => void,
}

interface ProfileEditValues {
    full_name: string;
    phone: string;
    personCountry?: any;
    personState?: any;
    personCity?: string;
    personZip?: string;
}

interface WorkAuthorization {
    authorized_to_work?: string;
    current_work_authorization?: string;
    sponsorship_required?: string;
    visa_expiry?: Dayjs;
}

interface ScreeningChecks {
    bgv_allowed?: boolean;
    drug_test_allowed?: boolean;
    personal_verification_allowed?: boolean;
}

interface ApplyJobFormValues {
    profile_information_changed: string;
    resume?: string;
    // relocation_preference: string;
    // agree_with_job_details: string;
    // can_meet_travel_requirements?: string;
    can_work_at_job_location: string;
    work_authorization: WorkAuthorization;
    screening_checks: ScreeningChecks;
    declaration_read: boolean;
}

const profileChangeOptions = [
    { label: 'No', value: 'No' },
    { label: 'Yes - Update Profile', value: 'Yes - Update Profile' },
];
const yesNoOptions = [
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' },
];
// const relocationOptions = [
//     { label: 'Yes', value: 'Yes' },
//     { label: 'No', value: 'No' },
//     { label: 'Depends', value: 'Depends' },
//     { label: 'Locally Available', value: 'Locally Available' },
// ];
const requiredMsg = (message: string) => [{ required: true, message }];
const mustBeCheckedRule = (message: string) => [{
    validator: (_: any, value: boolean) =>
        value ? Promise.resolve() : Promise.reject(new Error(message)),
}];
// Normalises a profile field for change detection: lookups compare by value, phones by digits only
const profileFieldKey = (value: any) => {
    if (value && typeof value === 'object') return String(value.value ?? '');
    return String(value ?? '').trim();
};
const profileEditFields: (keyof ProfileEditValues)[] = ['full_name', 'phone', 'personCountry', 'personState', 'personCity', 'personZip'];
const disablePastDates = (current: Dayjs) => current && current.startOf('day').isBefore(new Date().setHours(0, 0, 0, 0));

const ApplyJobModalPage: React.FC<IProps> = (props) => {
    const navigate = useNavigate();
    const [form] = Form.useForm<ApplyJobFormValues>();
    const [profileForm] = Form.useForm<ProfileEditValues>();
    let { title, isModalOpen, user, jobOrderId, selectedJob, updateUserCache } = props;
    const [loading, setLoading] = useState(false);
    const [resumeListOptions, setResumeListOptions] = useState<any[]>([]);
    const [resumeUploading, setResumeUploading] = useState(false);
    const [submittable, setSubmittable] = useState(false);
    const [profileSaving, setProfileSaving] = useState(false);
    const [isCaqModel, setCaqModel] = useState(false);
    const [appicationSfdcId, setAppicationSfdcId] = useState({});
    const [stateListOptions, setStateListOptions] = useState<any[]>([]);
    const [profileInitialValues, setProfileInitialValues] = useState<ProfileEditValues>();

    const profileInformationChanged = Form.useWatch('profile_information_changed', form);
    const editedPhone = Form.useWatch('phone', profileForm);
    const editedProfile: ProfileEditValues = {
        full_name: Form.useWatch('full_name', profileForm),
        phone: editedPhone,
        personCountry: Form.useWatch('personCountry', profileForm),
        personState: Form.useWatch('personState', profileForm),
        personCity: Form.useWatch('personCity', profileForm),
        personZip: Form.useWatch('personZip', profileForm),
    };
    const isProfileChanged = !!profileInitialValues && profileEditFields.some((field) => {
        const before = profileFieldKey(profileInitialValues[field]);
        const after = profileFieldKey(editedProfile[field]);
        return field === 'phone' ? before.replace(/\D/g, '') !== after.replace(/\D/g, '') : before !== after;
    });

    // Travel is only asked when the selected job actually carries a travel requirement.
    // const jobRequiresTravel = !!selectedJob?._source?.Travel_Requirement__c;

    const handleSubmit = () => {
        form.submit();
    };

    const handleCancel = () => {
        props.onCloseModal();
    };

    useEffect(() => {
        if (user && user._id) {
            setFormValues(user);
            loadResumeList(user._id);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const loadFreshUserProfile = () => {
        if (!user?._id) return;
        getPersonInfo(user._id).then((personData: any) => {
            if (personData?.data) {
                const freshUserData = personData.data.data;
                const freshProfile = personData.data.profile;
                if (freshUserData) {
                    updateUserCache({ ...user, ...freshUserData, profile: freshProfile });
                    localStorage.setItem("user", JSON.stringify(freshUserData));
                }
            }
        });
    };

    useEffect(() => {
        // The same drawer instance is reused across job applications, so start fresh each time it opens.
        if (!isModalOpen) return;
        setCaqModel(false);
        loadFreshUserProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModalOpen]);

    const hasConsent = (consentList: any, permissionType: string) => {
        return Array.isArray(consentList) && consentList.some((c: any) => c && c.permission_type === permissionType);
    };

    // Only the required fields drive submit-ability; optional ones don't block it.
    const computeSubmittable = (v: Partial<ApplyJobFormValues> | undefined) => {
        const requiredFilled = !!v?.can_work_at_job_location
            && !!v?.work_authorization?.authorized_to_work
            // && !!v?.relocation_preference
            // && !!v?.agree_with_job_details
            // && (!jobRequiresTravel || !!v?.can_meet_travel_requirements)
            && !!v?.work_authorization?.sponsorship_required
            && !!v?.resume;
        const checksOk = !!v?.screening_checks?.bgv_allowed
            && !!v?.screening_checks?.drug_test_allowed
            && !!v?.screening_checks?.personal_verification_allowed
            && !!v?.declaration_read;
        return requiredFilled && checksOk;
    };

    const refreshSubmittable = () => setSubmittable(computeSubmittable(form.getFieldsValue(true)));

    const onValuesChange = () => refreshSubmittable();

    const loadResumeList = async (profileId: string) => {
        try {
            setLoading(true);
            const resumeList: any = await getCadidateResume(profileId);
            setResumeListOptions(resumeList || []);
            if (resumeList && resumeList.length) {
                form.setFieldsValue({ resume: resumeList[0]['value'] });
            }
        } catch (error) {
            CustomeNotification('error', "Opps! Something went wrong please try again!");
        } finally {
            setLoading(false);
            refreshSubmittable();
        }
    }

    const setFormValues = (user: any) => {
        form.setFieldsValue({
            profile_information_changed: 'No',
            screening_checks: {
                bgv_allowed: hasConsent(user?.consent, 'BGV'),
                drug_test_allowed: hasConsent(user?.consent, 'DRUG_TEST'),
            },
        } as any);
        refreshSubmittable();
    }

    const onProfileChangeSelect = (value: string) => {
        if (value === 'Yes - Update Profile') {
            const address = user?.profile?.address || {};
            const initialValues: ProfileEditValues = {
                full_name: user?.full_name,
                phone: user?.phone,
                personCountry: address.country,
                personState: address.state,
                personCity: address.city,
                personZip: address.zip,
            };
            profileForm.setFieldsValue(initialValues);
            setProfileInitialValues(initialValues);
            loadStates(address.country);
        }
    };

    const loadStates = (country: any) => {
        setStateListOptions([]);
        if (country && country.value) {
            fetchSelectStateLookupList(country).then(setStateListOptions);
        }
    };

    const onCountryChange = (country: any) => {
        profileForm.setFieldsValue({ personState: undefined });
        loadStates(country);
    };

    // Reuses the same basic-info endpoint (and phone validation) as the full profile editor,
    // just inline so the candidate never leaves the application drawer.
    const onProfileEditFinish = async (values: ProfileEditValues) => {
        if (!user?._id) return;
        setProfileSaving(true);
        try {
            const address = {
                country: values.personCountry || '',
                state: values.personState || '',
                city: values.personCity || '',
                zip: values.personZip || '',
            };
            // prepairBasicInfo mutates its input (drops the person* keys), so pass a copy
            await updateProfile(user._id, { ...values }, 0);
            CustomeNotification('success', 'Profile updated successfully.');
            updateUserCache({ ...user, full_name: values.full_name, phone: values.phone, profile: { ...user?.profile, address } });
            form.setFieldsValue({ profile_information_changed: 'No' });
        } catch (error: any) {
            CustomeNotification('error', error?.message || 'Something went wrong. Please try again.');
        } finally {
            setProfileSaving(false);
        }
    };

    const uploadNewResume = async (file: RcFile): Promise<string> => {
        const res: any = await uploadResume(user._id, { file_name: file });
        const uploadedFileName = res?.data?.data?.fileName;
        if (!uploadedFileName) {
            throw new Error('Resume upload failed.');
        }
        return uploadedFileName;
    };

    const beforeResumeUpload = async (file: RcFile) => {
        const fileSizeMB = file.size / 1024 / 1024;
        if (fileSizeMB >= fileTypeAllow.resumeSize) {
            CustomeNotification('error', `File size is ${fileSizeMB.toFixed(2)} MB. Only ${fileTypeAllow.resumeSize} MB is allowed.`);
            return Upload.LIST_IGNORE;
        }
        try {
            setResumeUploading(true);
            const fileName = await uploadNewResume(file);
            // Append to the existing list rather than reloading it, and link it to this application.
            setResumeListOptions((prev) => [...prev, { label: file.name, value: fileName }]);
            form.setFieldsValue({ resume: fileName });
            refreshSubmittable();
            CustomeNotification('success', 'Resume uploaded successfully.');
        } catch (error) {
            CustomeNotification('error', 'Resume upload failed. Please try again.');
        } finally {
            setResumeUploading(false);
        }
        return Upload.LIST_IGNORE;
    };

    const onFormFinish = async (values: ApplyJobFormValues) => {
        let id = user._id || ''; //person_id
        let sfdcId = user.sfdc_id || '';

        setLoading(true);
        try {
            const applicationData = {
                applied_by_candidate: id,
                candidate_sfdcId: sfdcId,
                job_order_id: jobOrderId ? jobOrderId : 1,
                applied_on: new Date(),
                resume: values.resume,
                applied_as_guest: (user) ? false : true,
                bgv_consent_given: new Date(),
                guest_full_name: user?.full_name,
                guest_email: user?.email,
                guest_phone: user?.phone,
                job_title: selectedJob?._source?.Job_Title__c,
                job_location: selectedJob?._source?.Location_List_New__c,
                hiring_manager: selectedJob?._source?.Hiring_Manager__c,
                secondary_recruiter: selectedJob?._source?.Secondary_Recruiter__c,
                job_order_number: selectedJob?._source?.Job_Number__c,
                caq_submitted: false,
                internal_question_set: selectedJob?._source?.Internal_Question_Set__c,
                about_you: user?.profile?.about_you,

                // relocation_preference: values.relocation_preference,
                // agree_with_job_details: values.agree_with_job_details,
                // can_meet_travel_requirements: values.can_meet_travel_requirements,
                can_work_at_job_location: values.can_work_at_job_location,

                work_authorization: {
                    authorized_to_work: values.work_authorization?.authorized_to_work,
                    current_work_authorization: values.work_authorization?.current_work_authorization?.trim() || undefined,
                    sponsorship_required: values.work_authorization?.sponsorship_required,
                    visa_expiry: values.work_authorization?.visa_expiry ? values.work_authorization.visa_expiry.toDate() : undefined,
                },

                screening_checks: {
                    bgv_allowed: values.screening_checks?.bgv_allowed,
                    drug_test_allowed: values.screening_checks?.drug_test_allowed,
                    personal_verification_allowed: values.screening_checks?.personal_verification_allowed,
                },
                declaration_read: values.declaration_read,
            };

            updateConsent(id, values);
            await createApplication(applicationData);
        } catch (error: any) {
            setLoading(false);
            CustomeNotification('error', error?.message || "Opps! Something went wrong please try again!");
        }
    };

    const updateConsent = (id: any, values: ApplyJobFormValues) => {
        const consentArray = [];
        if (values.screening_checks?.bgv_allowed) {
            consentArray.push({ permission_type: 'BGV', last_permitted_on: new Date() })
        }
        if (values.screening_checks?.drug_test_allowed) {
            consentArray.push({ permission_type: 'DRUG_TEST', last_permitted_on: new Date() })
        }
        if (consentArray.length) {
            const profileData = {
                consent: consentArray
            }
            updateAboutProfile(id, profileData);
        }
    }

    const createApplication = (applicationData: any) => {
        return applyJob(applicationData).then((res: any) => {
            setLoading(false);
            if (res && res.sfdc_id) {
                postHogCaptureAnalitics('job_application', { app_id: res.sfdc_id, job_id: jobOrderId });
                CustomeNotification('success', `Job Applied Successfully.`);
                setAppicationSfdcId(res.sfdc_id || '')
                setCaqModel(true);
            } else {
                CustomeNotification('error', res.message ? res.message : "Opps! Something went wrong please try again!");
            }
        }).catch(err => {
            setLoading(false);
        });
    }

    const onFinishFailed = () => {
        // Ant Design's scrollToFirstError already focuses the offending field.
    };

    const submitNow = () => {
        handleCancel();
        //redirect on caq pages.
        const internalQuestionSet = selectedJob?._source?.Internal_Question_Set__c;
        if (appicationSfdcId && internalQuestionSet) {
            navigate('/person-caq/' + appicationSfdcId + '/' + internalQuestionSet, { replace: true });
        } else {
            CustomeNotification('error', "Opps! Something went wrong please try again!");
        }
    }

    const submitLater = async () => {
        handleCancel();
        //send caq to candidate on their email id
        const selectedJobInfo = selectedJob?._source || {};
        const url = environment.caqUrl + '/public/caq/' + appicationSfdcId + '/' + selectedJobInfo.Internal_Question_Set__c;
        const questionSet = selectedJobInfo.Internal_Question_Set__c ? selectedJobInfo.Internal_Question_Set__c : '';
        const queName = selectedJobInfo.internalQuestionSet && selectedJobInfo.internalQuestionSet.Name ? selectedJobInfo.internalQuestionSet.Name : '';
        const jobOrderNumber = selectedJobInfo.Job_Number__c ? selectedJobInfo.Job_Number__c : '';
        const jobTitle = selectedJobInfo.Job_Title__c ? selectedJobInfo.Job_Title__c : '';

        const dataObj = {
            appSfdcId: appicationSfdcId, url: url, queSet: questionSet,
            queName: queName,
            jobName: jobOrderNumber,
            jobTitle: jobTitle
        };

        const emailSend: any = await sendCAQEMail(dataObj);
        if (emailSend) {
            const data = {
                caq_submitted: false
            }
            updateJobApplication(data, appicationSfdcId);
            CustomeNotification('success', `Candidate assessment questionnaire email has been sent successfully.`);
        }
    }

    const drawerFooter = !isCaqModel ? (
        <Row justify="end" gutter={8}>
            <Col><Button onClick={handleCancel}>Cancel</Button></Col>
            <Col><Button type="primary" loading={loading} disabled={!submittable} onClick={handleSubmit}>Submit Application</Button></Col>
        </Row>
    ) : null;

    return (
        <Drawer
            title={title}
            open={isModalOpen}
            onClose={handleCancel}
            placement="right"
            width={720}
            rootClassName="apply-job-drawer"
            footer={drawerFooter}
            destroyOnClose
        >
            <Spin spinning={loading} tip="Loading...">
                {isCaqModel
                    ?
                    <Fragment>
                        <Row>
                            <Col span={24} className="TextCenter">
                                <Title className="mt-1" level={4} style={{ color: '#d9363e' }}>Your candidate application is incomplete!</Title>
                            </Col>
                        </Row>
                        <Row>
                            <Col span={24} className="TextCenter">
                                <Title className="mt-0" level={5} style={{ color: 'rgb(100, 116, 139)', }}>Please fill in and submit the assessment questionnaire to complete your application and increase your chance of getting hired.</Title>
                            </Col>
                        </Row>
                        <Divider />
                        <Row>
                            <Col span={24}>
                                <Button key="submitCAQNow" type="primary" className="mr-5 w-100 text-wrap" onClick={submitNow}>Submit Assessment Questionnaire Now </Button>
                                <Button className="w-100 mt-3" key="submitCAQLater" onClick={submitLater}>Submit Later</Button>
                            </Col>
                        </Row>
                    </Fragment>
                    :
                    <Form<ApplyJobFormValues>
                        layout="vertical"
                        autoComplete="off"
                        form={form}
                        validateMessages={validateMessages}
                        onFinish={onFormFinish}
                        onValuesChange={onValuesChange}
                        className="apply-form"
                        onFinishFailed={onFinishFailed}
                        scrollToFirstError
                    >
                        <div className="apply-profile-summary">
                            <Text strong style={{ fontSize: 16 }}>{user?.full_name || '-'}</Text><br />
                            <Text><InboxOutlined className="mr-2" />{user?.email || '-'}</Text><br />
                            <Text><PhoneOutlined className="mr-2" />{user?.phone || '-'}</Text><br />
                            <Text><EnvironmentOutlined className="mr-2" />{formatAddress(user?.profile?.address) || '-'}</Text>
                        </div>

                        <Row gutter={[16, 0]}>
                            <Col span={24}>
                                <Form.Item
                                    label={<Text strong>Has your contact or professional information changed?</Text>}
                                    name="profile_information_changed"
                                    rules={requiredMsg('Please select an option.')}
                                >
                                    <Radio.Group options={profileChangeOptions} optionType="button" buttonStyle="solid" onChange={(e) => onProfileChangeSelect(e.target.value)} />
                                </Form.Item>
                            </Col>
                            {profileInformationChanged === 'Yes - Update Profile' && (
                                <Col span={24}>
                                    <div className="apply-inline-profile-edit">
                                        <Form<ProfileEditValues> form={profileForm} layout="vertical" onFinish={onProfileEditFinish} component={false}>
                                            <Form.Item label="Full Name" name="full_name" rules={[{ required: true, message: 'Full name is required.' }, { max: 100 }]}>
                                                <Input placeholder="Full Name" />
                                            </Form.Item>
                                            <Form.Item
                                                label="Phone"
                                                name="phone"
                                                validateTrigger={['onChange', 'onBlur']}
                                                rules={[
                                                    { required: true, message: 'Phone number is required.' },
                                                    {
                                                        validator: (_, value) => !value || isValidPhoneNumber(value)
                                                            ? Promise.resolve()
                                                            : Promise.reject(new Error('Please enter a valid phone number.')),
                                                    },
                                                ]}
                                            >
                                                <PhoneInput
                                                    international
                                                    addInternationalOption={false}
                                                    defaultCountry="US"
                                                    value={editedPhone}
                                                    onChange={(value) => profileForm.setFieldsValue({ phone: value })}
                                                    className="input-phone-number"
                                                />
                                            </Form.Item>
                                            <Row gutter={[12, 0]}>
                                                <Col xs={24} sm={12}>
                                                    <Form.Item label="Country" name="personCountry">
                                                        <DebounceSelectLookup
                                                            controlName="country"
                                                            placeholder="Search country.."
                                                            fetchOptions={fetchSelectLookupList}
                                                            onChange={onCountryChange}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={24} sm={12}>
                                                    <Form.Item label="State" name="personState">
                                                        <Select options={stateListOptions} labelInValue placeholder="State" />
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={24} sm={12}>
                                                    <Form.Item label="City" name="personCity" rules={[{ max: 100 }]}>
                                                        <Input placeholder="City" maxLength={100} />
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={24} sm={12}>
                                                    <Form.Item label="Zip/Postal Code" name="personZip" rules={[{ max: 20 }]}>
                                                        <Input placeholder="Postal Code" maxLength={20} />
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                            <Row justify="end" gutter={8}>
                                                <Col>
                                                    <Button size="small" onClick={() => form.setFieldsValue({ profile_information_changed: 'No' })}>Cancel</Button>
                                                </Col>
                                                <Col>
                                                    <Button size="small" type="primary" loading={profileSaving} disabled={!isProfileChanged} onClick={() => profileForm.submit()}>Save</Button>
                                                </Col>
                                            </Row>
                                        </Form>
                                        {/* <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
                                            Need to update education, employment or skills instead?{' '}
                                            <Button type="link" size="small" style={{ padding: 0, height: 'auto' }} onClick={() => window.open('/edit-profile', '_blank')}>Edit full profile</Button>
                                        </Text> */}
                                    </div>
                                </Col>
                            )}
                        </Row>

                        <Divider />
                        <Title level={5} className="apply-section-title">Resume</Title>
                        <Row gutter={[16, 0]}>
                            <Col span={24}>
                                {/* <div className="ant-form-item-label apply-resume-label"><label>Resume</label></div> */}
                                <div className="apply-resume-row">
                                    <Form.Item
                                        name="resume"
                                        rules={requiredMsg('Please select or upload a resume.')}
                                        style={{ flex: 1, marginBottom: 0 }}
                                    >
                                        <Select
                                            placeholder="Select a resume"
                                            options={resumeListOptions}
                                            notFoundContent="No saved resume yet. Use Upload Resume to add one."
                                        />
                                    </Form.Item>
                                    <span className="apply-resume-or" aria-hidden="true">or</span>
                                    <Upload accept={fileTypeAllow.resume} maxCount={1} multiple={false} showUploadList={false} beforeUpload={beforeResumeUpload} disabled={resumeUploading}>
                                        <Button icon={<UploadOutlined />} loading={resumeUploading} title="Upload a new resume">Upload Resume</Button>
                                    </Upload>
                                </div>
                            </Col>
                        </Row>

                        {/* <Divider />
                        <Title level={5} className="apply-section-title">Job Preferences</Title>
                        <Row gutter={[16, 0]}>
                            <Col span={24}>
                                <Form.Item
                                    label="If required, are you open to relocation for this opportunity?"
                                    name="relocation_preference"
                                    rules={requiredMsg('Please select an option.')}
                                >
                                    <Radio.Group options={relocationOptions} optionType="button" buttonStyle="solid" />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item
                                    label="Do you agree with the job details described in this job description?"
                                    name="agree_with_job_details"
                                    rules={requiredMsg('Please select an option.')}
                                >
                                    <Radio.Group options={yesNoOptions} optionType="button" buttonStyle="solid" />
                                </Form.Item>
                            </Col>
                            {jobRequiresTravel && (
                                <Col span={24}>
                                    <Form.Item
                                        label="Are you able to meet the travel requirements of this position?"
                                        name="can_meet_travel_requirements"
                                        rules={requiredMsg('Please select an option.')}
                                    >
                                        <Radio.Group options={yesNoOptions} optionType="button" buttonStyle="solid" />
                                    </Form.Item>
                                </Col>
                            )}
                        </Row> */}

                        <Divider />
                        <Title level={5} className="apply-section-title">Location</Title>
                        <Row gutter={[16, 0]}>
                            <Col span={24}>
                                <Form.Item
                                    label="Are you currently able to work at the location specified for this position?"
                                    name="can_work_at_job_location"
                                    rules={requiredMsg('Please select an option.')}
                                >
                                    <Radio.Group options={yesNoOptions} optionType="button" buttonStyle="solid" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider />
                        <Title level={5} className="apply-section-title">Work Authorization</Title>
                        <Row gutter={[16, 0]}>
                            <Col span={24}>
                                <Form.Item
                                    label="Are you legally authorized to work in the country where this job is located?"
                                    name={['work_authorization', 'authorized_to_work']}
                                    rules={requiredMsg('Please select an option.')}
                                >
                                    <Radio.Group options={yesNoOptions} optionType="button" buttonStyle="solid" />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item
                                    label="Current Work Authorization"
                                    name={['work_authorization', 'current_work_authorization']}
                                    rules={[{ max: 100, message: 'Work authorization cannot exceed 100 characters.' }]}
                                >
                                    <Input placeholder="Enter your current work authorization" maxLength={100} />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item
                                    label="Do you currently or in the future require employer sponsorship or immigration support?"
                                    name={['work_authorization', 'sponsorship_required']}
                                    rules={requiredMsg('Please select an option.')}
                                >
                                    <Radio.Group options={yesNoOptions} optionType="button" buttonStyle="solid" />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item
                                    label="Work Authorization / Visa Expiry, if applicable"
                                    name={['work_authorization', 'visa_expiry']}
                                >
                                    <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" disabledDate={disablePastDates} placeholder="Select Date" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider />
                        <Title level={5} className="apply-section-title">Checks & Screening<Text type="danger" style={{ fontSize: 14, verticalAlign: '0.1em', marginLeft: 1 }}>*</Text></Title>
                        <Row gutter={[16, 0]}>
                            <Col span={24}>
                                <Form.Item
                                    name={['screening_checks', 'bgv_allowed']}
                                    valuePropName="checked"
                                    rules={mustBeCheckedRule('Please confirm the background verification consent.')}
                                >
                                    <Checkbox>
                                        I understand that, if required for this position, I may be asked to undergo a background verification, which may include criminal record checks where legally permitted. Any such verification will be conducted in accordance with applicable local laws and regulations.
                                    </Checkbox>
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item
                                    name={['screening_checks', 'drug_test_allowed']}
                                    valuePropName="checked"
                                    rules={mustBeCheckedRule('Please confirm the drug screening consent.')}
                                >
                                    <Checkbox>
                                        I understand that, if required for this position and permitted under applicable local laws and regulations, I may be asked to complete a drug screening. I am willing to participate in such screening if required for the role.
                                    </Checkbox>
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item
                                    name={['screening_checks', 'personal_verification_allowed']}
                                    valuePropName="checked"
                                    rules={mustBeCheckedRule('Please confirm the education and employment verification consent.')}
                                >
                                    <Checkbox>
                                        I understand that, if required for this position, information relating to my education and previous employment may be verified. Any such verification will be conducted in accordance with applicable local laws and regulations.
                                    </Checkbox>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider />
                        <Title level={5} className="apply-section-title">Declaration<Text type="danger" style={{ fontSize: 14, verticalAlign: '0.1em', marginLeft: 1 }}>*</Text></Title>
                        <Row gutter={[16, 0]}>
                            <Col span={24}>
                                <Form.Item
                                    name="declaration_read"
                                    valuePropName="checked"
                                    rules={mustBeCheckedRule('Please confirm before submitting your application.')}
                                >
                                    <Checkbox>
                                        Information provided in this application is accurate to the best of my knowledge.
                                    </Checkbox>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                }
            </Spin>
        </Drawer>
    );
};


const mapStateToProps = (state: any) => {
    return {
        user: state.reducer.auth.user,
        selectedJob: state.reducer.job.selectedJob
    }
};

function mapDispatchToProps(dispatch: any) {
    return { updateUserCache: (payload: any) => dispatch(UserCacheUpdate(payload)) };
}

export default connect(mapStateToProps, mapDispatchToProps)(ApplyJobModalPage)
